/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useEffect, useRef } from 'react';

import { css, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';

type Props = {
	children: ReactNode;
	close?: () => void;
	selectedTabId?: string | null;
};

export const MoreMenuContents = ({ children, close, selectedTabId }: Props) => {
	const menuContentsRef = useRef<HTMLDivElement | HTMLUListElement>(null);

	const previousSelectedTabId = useRef(selectedTabId);
	useEffect(() => {
		if (selectedTabId !== previousSelectedTabId.current && close) {
			close();
		} else {
			previousSelectedTabId.current = selectedTabId;
		}
	}, [selectedTabId, close]);

	useEffect(() => {
		if (!menuContentsRef.current) {
			return;
		}

		autoScrollForElements({
			element: menuContentsRef.current,
		});
	}, []);

	if (fg('jfp_a11y_team_board_missing_unordered_list_mark-up')) {
		return (
			// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-noninteractive-element-interactions
			<ul
				css={moreMenuContentsUlStyles}
				ref={menuContentsRef as React.Ref<HTMLUListElement>}
				onClick={(e) => {
					e.stopPropagation();
				}}
			>
				{children}
			</ul>
		);
	}
	return (
		// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlassian/a11y/no-static-element-interactions
		<div
			css={moreMenuContentsStyles}
			ref={menuContentsRef as React.Ref<HTMLDivElement>}
			onClick={(e) => {
				e.stopPropagation();
			}}
		>
			{children}
		</div>
	);
};

const moreMenuContentsStyles = css({
	maxHeight: '60vh',
	overflowY: 'auto',
	// We need to add some padding in the menu so the drag previews at the top/bottom don't
	// show a scroll wheel when added. We need to use 1px, since any more would introduce whitespace.
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingBlock: '1px',
});

const moreMenuContentsUlStyles = css({
	maxHeight: '60vh',
	overflowY: 'auto',
	listStyleType: 'none',
	padding: 0,
	// We need to add some padding in the menu so the drag previews at the top/bottom don't
	// show a scroll wheel when added. We need to use 1px, since any more would introduce whitespace.
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingBlock: '1px',
});
